import InfiniteScroll from 'react-infinite-scroll-component';
import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useBuildNotificationFromJson } from '@/modules/common/hooks/useBuildNotificationFromJson.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerReadNotificationModal } from '@/modules/issuer-dashboard/components/modals/IssuerReadNotificationModal.tsx';
import {
  useIssuerMakeIsReadNotificationsMutation,
  useIssuerNotificationsListQuery,
} from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { ChartEmptyState, Spinner } from 'ui';
import { fixNameEnum } from 'ui/utils';
import { EventLogResponse, EventLogType } from 'api';

interface NAVUpdateLogsListProps {
  register_class_id?: number;
  limit?: number;
  createdDates?: [Dayjs | null, Dayjs | null] | null;
  blockIdInfinite?: string;
}

export const NAVUpdateLogsList = ({
  blockIdInfinite,
  limit = 15,
  createdDates,
  register_class_id,
}: NAVUpdateLogsListProps) => {
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const {
    data: dataNotificationsList,
    fetchNextPage,
    hasNextPage,
    isError,
    isLoading,
  } = useIssuerNotificationsListQuery(
    {
      page: 1,
      page_size: limit,
      from_date: createdDates?.[0] ? dayjs(createdDates[0]).format('YYYY-MM-DD') : undefined,
      to_date: createdDates?.[1] ? dayjs(createdDates[1]).format('YYYY-MM-DD') : undefined,
      types: [EventLogType.NavUpdated, EventLogType.NavCreated, EventLogType.NavDeleted].join(','),
      register_class_id: register_class_id || undefined,
    },
    registerId,
  );

  const notifications = useMemo(
    () => (dataNotificationsList?.pages || []).flatMap(({ data }) => data),
    [dataNotificationsList],
  );

  return (
    <InfiniteScroll
      next={fetchNextPage}
      dataLength={notifications.length}
      hasMore={!!hasNextPage}
      style={{ overflow: 'unset' }}
      loader={
        <div className="flex justify-center items-center h-16 w-full">
          <Spinner size="md" variant="primary" />
        </div>
      }
      scrollableTarget={blockIdInfinite}
    >
      <div className="w-full flex flex-col">
        {!notifications?.length && (
          <div className="py-4 flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isError={isError} isLoading={isLoading} />
          </div>
        )}
        {notifications?.map(notification => (
          <NAVUpdateLogItem key={notification.id} notification={notification} />
        ))}
      </div>
    </InfiniteScroll>
  );
};

interface NAVUpdateLogItemProps {
  notification: EventLogResponse;
  inModal?: boolean;
}

export const NAVUpdateLogItem = ({ notification, inModal = false }: NAVUpdateLogItemProps) => {
  const [openNotification, setOpenNotification] = useState(false);
  const getRegisterTypeString = useRegisterTypeStrings();
  const { mutateAsync } = useIssuerMakeIsReadNotificationsMutation();
  const text = useBuildNotificationFromJson(
    notification.type,
    notification.data,
    !inModal,
    rolesManipulator.current_user_role === Role.SR_ISSUER ||
      rolesManipulator.current_user_role === Role.SR_ISSUER_ADMIN,
  );

  const isActive = (notification?.data as any)?.['is_active'] ?? false;
  const isHistorical = (notification?.data as any)?.['is_historical'] ?? false;
  const isFuture = (notification?.data as any)?.['is_future'] ?? false;

  const handleOpenModal = async () => {
    setOpenNotification(true);
    if (!notification.is_read) {
      await mutateAsync({ ids: [notification.id] });
    }
  };

  return (
    <>
      <div
        className={classNames(
          'flex justify-between items-start py-2',
          'hover:bg-gray-50 cursor-pointer',
          {
            'border-t border-t-gray-200 gap-4': !inModal,
            'flex-col gap-2': inModal,
          },
        )}
        onClick={handleOpenModal}
      >
        <div className="flex flex-col">
          <div className="text-sm leading-[22px] font-[500] text-gray-900 flex items-center gap-4">
            <span>
              {isActive ? 'Active' : isHistorical ? 'Historical' : isFuture ? 'Future' : ''}{' '}
              {fixNameEnum(notification.type)
                .replace('Nav', getRegisterTypeString('nav', { capitalize: true }))
                .replace('Dividend', getRegisterTypeString('Dividend', { capitalize: true }))
                .replace('Finalized', 'Finalised')}
            </span>
            <div
              className={`w-2 h-2 rounded-full ${
                notification.is_read ? 'bg-gray-200' : 'bg-red-600'
              }`}
            />
          </div>
          <div className="text-xs leading-5 text-gray-400">{text}</div>
        </div>
        <div className="text-xs leading-5 text-[#737373]">
          {dateToPrint(
            notification?.created_at,
            inModal ? DateVariant.Table : DateVariant.Dashboard,
          ) || '-'}
        </div>
      </div>
      {openNotification && (
        <IssuerReadNotificationModal
          open={openNotification}
          onClose={() => {
            setOpenNotification(false);
          }}
          {...{
            type: notification.type,
            data: notification.data,
            created_at: dateToPrint(notification?.created_at) || '-',
          }}
        />
      )}
    </>
  );
};
